import { graphql, useStaticQuery } from "gatsby";
import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageContext, text } from "../../../container/language";
import { calculateDepositReceiveable } from "../../../utils/deposit";
import numeral from "numeral";

import "../style.scss";
import styled from "styled-components";
import TitleWithFirstLetter from "../../TitleWithFirstLetter";
import Breadcrumbs from "../../Breadcrumbs/index";

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableData = styled.td`
  padding: 1em;
  font-size: 1.1rem;
  color: #004181;
  font-weight: 600;
  border: 1px solid #dddddd;
`;

const FibankForm = styled.div`
  width: 50%;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 100%;
    padding: 1em 2em;
  }
`

const DepositForm = () => {
  const { t } = useTranslation();
  const { userLanguage } = useContext(LanguageContext);
  const {
    api: { toolsDeposits },
  } = useStaticQuery(
    graphql`
      query {
        api {
          toolsDeposits(sort: "displayOrder:asc,id:asc") {
            id
            displayOrder
            name {
              id
              text_sq
              text_en
            }
            currency
            months
            interestRate
          }
        }
      }
    `
  );
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedMonths, setSelectedMonths] = useState(0);
  const [selectedDepositID, setSelectedDepositID] = useState(0);
  const [amount, setAmount] = useState(0);
  const [interestVal, setInterestVal] = useState("");
  const [totalAmountVal, setTotalAmountVal] = useState("");
  const [calculatedDeposit, setCalculatedDeposit] = useState(false);

  const currencies = useMemo(() => {
    return Object.values(
      toolsDeposits.reduce((acc, d) => {
        acc[d.currency] = d.currency;
        return acc;
      }, {})
    ).map((c) => ({ text: c, value: c }));
  }, [toolsDeposits]);

  const months = useMemo(() => {
    return Object.values(
      toolsDeposits.reduce((acc, d) => {
        acc[d.months] = d.months;
        return acc;
      }, {})
    )
      .map((c) => ({
        text: c.replace("_", ""),
        value: Number(c.replace("_", "")),
      }))
      .sort((a, b) => a.value - b.value);
  }, [toolsDeposits]);

  const filteredDeposits = useMemo(() => {
    return toolsDeposits.filter(
      (d) =>
        d.currency === selectedCurrency && d.months === `_${selectedMonths}`
    );
  }, [selectedCurrency, selectedMonths]);

  const calculateDeposit = () => {
    const deposit = filteredDeposits.find((d) => d.id === selectedDepositID);
    const { interestAmount, totalAmount } = calculateDepositReceiveable(
      deposit.interestRate,
      selectedMonths,
      amount
    );
    setInterestVal(interestAmount);
    setTotalAmountVal(totalAmount);
    setCalculatedDeposit(true);
  };

  const categories = {
    title: {
      text_sq: t("tools.deposit"),
      text_en: t("tools.deposit"),
    },
    slug: "about",
    parentCategory: {
      title: {
        text_sq: t("tools.title"),
        text_en: t("tools.title"),
      },
      slug: "tools",
    },
  };

  return (
    <div>
      <section>
        <div className="wrapper">
          <Breadcrumbs data={categories} />
        </div>
      </section>
      <FibankForm
        className="fibank-tools-deposit form"
      >
        <TitleWithFirstLetter title={t("pages.depositCalculator.title")} />
        <div className="form-input mt-1">
          <label className="title title-18">
            <font>{t("pages.depositCalculator.currency")}</font>
          </label>
          <select
            value={selectedCurrency}
            onChange={(e) => {
              setSelectedCurrency(e.target.value);
              setSelectedDepositID(0);
              setCalculatedDeposit(false);
            }}
          >
            <option disabled value="">
              {t("pages.depositCalculator.choose")}
            </option>
            {currencies.map(({ text, value }) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </select>
        </div>

        <div className="form-input mt-2">
          <label className="title title-18">
            <font>{t("pages.depositCalculator.months")}</font>
          </label>
          <select
            value={selectedMonths}
            onChange={(e) => {
              setSelectedMonths(e.target.value);
              setSelectedDepositID(0);
              setCalculatedDeposit(false);
            }}
          >
            <option disabled value={0}>
              {t("pages.depositCalculator.choose")}
            </option>
            {months.map(({ text, value }) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </select>
        </div>

        <div className="form-input mt-2">
          <label className="title title-18">
            <font>{t("pages.depositCalculator.kind")}</font>
          </label>
          <select
            value={selectedDepositID}
            disabled={!selectedCurrency || !selectedMonths}
            onChange={(e) => {
              setSelectedDepositID(e.target.value);
              setCalculatedDeposit(false);
            }}
          >
            <option disabled value={0}>
              {t("pages.depositCalculator.choose")}
            </option>
            {filteredDeposits.map(({ name, id }) => (
              <option key={id} value={id}>
                {text(name, userLanguage)}
              </option>
            ))}
          </select>
        </div>

        <div className="col col-100 mt-2">
          <div className="form-input">
            <label className="title title-18">
              <font>{t("pages.depositCalculator.sum")}</font>
            </label>
            <input
              autoComplete="off"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>

        <div className="col col-100 center" style={{ paddingTop: "20px" }}>
          <button
            disabled={
              !selectedCurrency ||
              !selectedMonths ||
              !selectedDepositID ||
              Number(amount) <= 0
            }
            onClick={calculateDeposit}
            type="button"
            className="apply-btn calc"
          >
            <font>
              <font>{t("pages.depositCalculator.calculate")}</font>
            </font>
          </button>
        </div>
        <div className="form-input">
          {calculatedDeposit && (
            <Table className="mt-1">
              <tbody>
                <tr>
                  <TableData>{t("pages.depositCalculator.interest")}</TableData>
                  <TableData>{`${numeral(interestVal).format(
                    "0,0"
                  )} ${selectedCurrency}`}</TableData>
                </tr>
                <tr>
                  <TableData>{t("pages.depositCalculator.total")}</TableData>
                  <TableData>{`${numeral(totalAmountVal).format(
                    "0,0"
                  )} ${selectedCurrency}`}</TableData>
                </tr>
              </tbody>
            </Table>
          )}
        </div>
      </FibankForm>
    </div>
  );
};

export default DepositForm;
